import { Link, useLocation, useNavigate } from "react-router";
import { useCallback, useEffect, useState, type FormEvent } from "react";
import { UserPlus } from "lucide-react";
import { KYC_STATUS_LABELS } from "@gatiq/shared";
import type { Paginated, UserDto } from "@gatiq/shared";
import {
  Alert,
  Badge,
  Button,
  Card,
  EmptyState,
  Page,
  PageHeader,
  Pagination,
  SearchInput,
  TD,
  TH,
  THead,
  TR,
  Modal,
  Table,
  TableSkeleton,
  TextInput,
  Toolbar,
  ToolbarCount,
  statusTone,
  useToast,
} from "@/ui";
import { api, ApiClientError } from "@/lib/api";
import { ListFilters, useListFilters } from "@/components/ListFilters";
import { KarigarPlaceFields } from "@/components/KarigarPlaceFields";
import { useCan } from "@/auth/access";
import { useAuth } from "@/auth/AuthProvider";

const PAGE_SIZE = 20;

const EMPTY = { name: "", phone: "", address: "", city: "", state: "", pincode: "", district: "", village: "" };

export function CounterKarigarsPage() {
  const toast = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const canAdd = Boolean(useCan()("karigars", "create"));
  const { user } = useAuth();
  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const filters = useListFilters(() => setPage(1));
  const [data, setData] = useState<Paginated<UserDto> | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const query = new URLSearchParams({ page: String(page), pageSize: String(PAGE_SIZE) });
      filters.apply(query);
      if (q.trim()) query.set("q", q.trim());
      const result = await api.get<Paginated<UserDto>>(`/org/me/karigars?${query.toString()}`);
      setData(result);
    } catch (err) {
      toast.error(err instanceof ApiClientError ? err.message : "Could not load your karigars.");
    } finally {
      setLoading(false);
    }
  }, [page, q, filters.apply, toast]);

  useEffect(() => {
    void load();
  }, [load]);

  async function addKarigar(e: FormEvent): Promise<void> {
    e.preventDefault();
    setFormError(null);
    setSaving(true);
    try {
      /*
       * `counterId` is sent because the shared schema requires it, but the
       * server overwrites it with the caller's own id — a counter can only
       * ever add to their own shop.
       */
      await api.post("/org/karigars", {
        name: form.name.trim(),
        phone: form.phone.trim(),
        counterId: user?.id,
        city: form.city,
        state: form.state,
        pincode: form.pincode,
        district: form.district,
        village: form.village,
        ...(form.address.trim() ? { address: form.address.trim() } : {}),
      });
      toast.success(`${form.name.trim()} added — waiting for approval.`);
      setAddOpen(false);
      await load();
    } catch (err) {
      setFormError(err instanceof ApiClientError ? err.message : "Could not add that karigar.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <Page bleed>
      <PageHeader
        title="My karigars"
      />

      <Card fill>
        <Toolbar
          trailing={
            <>
              {data ? <ToolbarCount count={data.total} noun="karigar" /> : null}
              {canAdd ? (
                <Button
                  icon={<UserPlus aria-hidden="true" className="size-4" />}
                  onClick={() => {
                    setForm(EMPTY);
                    setFormError(null);
                    setAddOpen(true);
                  }}
                >
                  Add karigar
                </Button>
              ) : null}
            </>
          }
        >
          <SearchInput
            label="Search karigars"
            onChange={(value) => {
              setQ(value);
              setPage(1);
            }}
            placeholder="Search by name or phone…"
            value={q}
          />
          <ListFilters definitions={data?.filters} filters={filters} />
        </Toolbar>

        {loading ? (
          <div className="min-h-0 flex-1 overflow-y-auto">
            <TableSkeleton cols={4} />
          </div>
        ) : !data || data.items.length === 0 ? (
          <div className="min-h-0 flex-1 overflow-y-auto">
            <EmptyState message="Karigars will appear here once they scan your registration QR." title="No karigars yet" />
          </div>
        ) : (
          <Table fill searchable={false} filterable={false}>
            <THead>
              <TR>
                <TH>Name</TH>
                <TH>Phone</TH>
                <TH>ID</TH>
                <TH>KYC</TH>
              </TR>
            </THead>
            <tbody>
              {data.items.map((k) => (
                <TR key={k.id} onClick={() => navigate(`${location.pathname}/${k.id}`)}>
                  <TD><Link className="font-medium text-brand-ink hover:underline" to={`${location.pathname}/${k.id}`} onClick={(event) => event.stopPropagation()}>{k.name}</Link></TD>
                  <TD>{k.phone}</TD>
                  <TD className="font-mono text-xs">{k.displayId}</TD>
                  <TD>
                    <Badge tone={statusTone(k.kycStatus ?? "")}>
                      {k.kycStatus ? KYC_STATUS_LABELS[k.kycStatus] : "—"}
                    </Badge>
                  </TD>
                </TR>
              ))}
            </tbody>
          </Table>
        )}
        {data ? (
          <div className="shrink-0">
            <Pagination onChange={setPage} page={data.page} total={data.total} totalPages={data.totalPages} />
          </div>
        ) : null}
      </Card>
    
      <Modal onClose={() => setAddOpen(false)} open={addOpen} title="Add a karigar">
        <form className="flex flex-col gap-4" noValidate onSubmit={(e) => void addKarigar(e)}>
          {formError ? <Alert kind="danger">{formError}</Alert> : null}

          <TextInput
            autoFocus
            label="Name"
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            required
            value={form.name}
          />
          <TextInput
            inputMode="numeric"
            label="Mobile number"
            onChange={(e) => setForm({ ...form, phone: e.target.value })}
            placeholder="98765 43210"
            required
            value={form.phone}
          />
          <KarigarPlaceFields value={form} onChange={(patch) => setForm((p) => ({ ...p, ...patch }))} />
          <div className="flex justify-end gap-2">
            <Button onClick={() => setAddOpen(false)} type="button" variant="secondary">
              Cancel
            </Button>
            <Button disabled={!form.name.trim() || !form.phone.trim()} loading={saving} type="submit">
              Add karigar
            </Button>
          </div>
        </form>
      </Modal>
    </Page>
  );
}
